/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Input,
  Modal,
  Button,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import IntlMessages from 'util/IntlMessages';
import InfoView from 'components/common/InfoView';
import {
  getRoles,
  getAllUsers,
  updateUser,
  addUser,
  setSelectedUserId,
} from '../../redux/actions/Auth';

import { userColumns } from '../../components/common/Columns';
import UserForm from './UserForm';
import generateQuery, { handleSearch as search } from '../../util/table';
import Can from '../../util/can';
import { USERS } from '../../constants/resources';
import useForm from '../../hooks/useForm';

const Users = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { roles } = useSelector(({ auth }) => auth);
  const {
    data: users,
    pageSize,
    updatingUser,
    addingUser,
    totalItems,
    loading,
    currentPage: current,
  } = useSelector(({ auth }) => auth.users);
  const [approvedUserId, setApprovedUserId] = useState('');
  const [verifiedUserId, setVerifiedUserId] = useState('');
  const [suspendedUserId, setSuspendedUserId] = useState('');
  const [searchParams, setSearchParams] = useState('');

  const {
    form,
    onFormOk,
    formVisibility,
    handleShowModal,
    handleCancel,
    handleEditRecord,
    editableRecord,
  } = useForm();

  useEffect(() => {
    dispatch(getRoles());
  }, [dispatch]);

  useEffect(() => {
    const pagination = `page=${current}&limit=${pageSize}`;
    dispatch(getAllUsers(pagination));
    // eslint-disable-next-line
  }, [dispatch]);

  const editable = Object.keys(editableRecord).length !== 0;

  const handleSubmit = (values) => {
    if (editable) {
      dispatch(updateUser(editableRecord._id, values));
    } else {
      const userData = {
        ...values,
        username: values.email
      };
      dispatch(addUser(userData));
    }
  };

  const navigateToUserMerchantsView = (record) => {
    dispatch(setSelectedUserId(record));
    history.push(`/merchant-accounts/users/${record._id}`);
  };

  const handleApproveUser = (record) => {
    const data = {
      is_active: !record.is_active,
    };
    setApprovedUserId(record._id);
    dispatch(updateUser(record._id, data));
    setTimeout(() => {
      setApprovedUserId('');
    }, 2000);
  };

  const handleVerifyUser = (record) => {
    const data = {
      is_verified: !record.is_verified,
    };
    setVerifiedUserId(record._id);
    dispatch(updateUser(record._id, data));
    setTimeout(() => {
      setVerifiedUserId('');
    }, 2000);
  };

  const handleSuspendUser = (record) => {
    const data = {
      is_suspended: !record.is_suspended,
    };
    setSuspendedUserId(record._id);
    dispatch(updateUser(record._id, data));
    setTimeout(() => {
      setSuspendedUserId('');
    }, 2000);
  };

  const handleOnChange = (pagination, filters, sorter) => {
    dispatch(getAllUsers(generateQuery(pagination, filters, sorter, searchParams)));
  };

  const handleSearch = (searchValue) => {
    const query = search(
      searchValue.trim().replace(/([^a-zA-Z0-9])/g, '\\$1'),
      dispatch, getAllUsers, userColumns,
    );
    setSearchParams(query);
  };

  const props = {
    loading: (loading || roles.loading),
    pagination: {
      current,
      pageSize,
      total: totalItems,
      showSizeChanger: false,
    },
    rowKey: '_id',
    onChange: handleOnChange,
    columns: userColumns(
      handleEditRecord,
      handleApproveUser,
      handleVerifyUser,
      handleSuspendUser,
      navigateToUserMerchantsView,
      users.updatingUser,
      approvedUserId,
      verifiedUserId,
      suspendedUserId,
      roles.data,
    ),
    dataSource: users.length > 0 ? users : [],
    scroll: { x: 1300 },
  };

  return (
    (
      <div>
        <h2 className="title gx-mb-4"><IntlMessages id="users.title" /></h2>
        <div className="gx-d-flex justify-content-center">
          <Can I="create" a={USERS}>
            <Button
              icon={<PlusOutlined />}
              data-testid="add-user-button"
              type="primary"
              onClick={() => handleShowModal()}
            >
              Add User
            </Button>
          </Can>
          <Input.Search
            placeholder="Search user accounts"
            enterButton
            data-testid="search-input"
            allowClear
            onSearch={handleSearch}
          />
        </div>
        <div className="justify-content-center">
          <Table data-testid="users-table" className="gx-table-responsive" {...props} />
        </div>
        <Modal
          title={editable ? 'Edit User' : 'Add User'}
          open={formVisibility}
          okText={editable ? 'Save' : 'Add'}
          onOk={() => onFormOk(handleSubmit)}
          confirmLoading={updatingUser || addingUser}
          onCancel={handleCancel}
        >
          <UserForm
            newRecord={!editable}
            form={form}
            roles={roles.data}
          />
        </Modal>
        <InfoView />
      </div>
    )
  );
};

export default Users;
